import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { toast } from "sonner";

export function useCellSelection(records, columns) {
    const [anchor, setAnchor] = useState(null);
    const [selected, setSelected] = useState(new Set());

    // Stabilize column names so callbacks don't recreate on every columns reference change
    const colNames = useMemo(() => columns.map((c) => c.name), [columns]);

    // Use refs for values only needed inside event handlers (not for rendering)
    const recordsRef = useRef(records);
    useEffect(() => { recordsRef.current = records; }, [records]);
    const colNamesRef = useRef(colNames);
    useEffect(() => { colNamesRef.current = colNames; }, [colNames]);

    const selectCell = useCallback((rowIndex, columnName, event) => {
        const key = `${rowIndex}:${columnName}`;

        if (event?.shiftKey && anchor) {
            const [anchorRow] = anchor.split(":").map((v, i) => (i === 0 ? parseInt(v) : v));
            const anchorCol = anchor.split(":")[1];
            const names = colNamesRef.current;
            const startRow = Math.min(anchorRow, rowIndex);
            const endRow = Math.max(anchorRow, rowIndex);
            const startCol = Math.min(names.indexOf(anchorCol), names.indexOf(columnName));
            const endCol = Math.max(names.indexOf(anchorCol), names.indexOf(columnName));

            const newSelected = new Set();
            for (let r = startRow; r <= endRow; r++) {
                for (let c = startCol; c <= endCol; c++) {
                    newSelected.add(`${r}:${names[c]}`);
                }
            }
            setSelected(newSelected);
        } else if (event?.ctrlKey || event?.metaKey) {
            setSelected((prev) => {
                const next = new Set(prev);
                next.has(key) ? next.delete(key) : next.add(key);
                return next;
            });
            setAnchor(key);
        } else {
            setSelected(new Set([key]));
            setAnchor(key);
        }
    }, [anchor]);

    const clearSelection = useCallback(() => {
        setSelected(new Set());
        setAnchor(null);
    }, []);

    const copySelection = useCallback(() => {
        if (selected.size === 0) return;
        const currentRecords = recordsRef.current;
        const names = colNamesRef.current;
        const cells = Array.from(selected).map((k) => {
            const [row, col] = [parseInt(k.split(":")[0]), k.split(":")[1]];
            return { row, col, value: currentRecords[row]?.[col] };
        });
        const rows = [...new Set(cells.map((c) => c.row))].sort((a, b) => a - b);
        const cols = names.filter((c) => cells.some((cell) => cell.col === c));
        const tsv = rows.map((r) => cols.map((c) => {
            const cell = cells.find((ce) => ce.row === r && ce.col === c);
            const v = cell?.value;
            return v == null ? "" : typeof v === "object" ? JSON.stringify(v) : String(v);
        }).join("\t")).join("\n");
        navigator.clipboard.writeText(tsv);
        toast.success("Copied to clipboard");
    }, [selected]);

    const isSelected = useCallback((rowIndex, columnName) => selected.has(`${rowIndex}:${columnName}`), [selected]);

    return { selected, selectCell, clearSelection, copySelection, isSelected };
}
